import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, Plus, Trash2 } from "lucide-react";
import { useTenant } from "@/hooks/useTenant";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export function EmailIntegrationTab() {
  const { orgId } = useTenant();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch email integrations
  const { data: integrations } = useQuery({
    queryKey: ["email-integrations", orgId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("email_integrations")
        .select("*")
        .eq("org_id", orgId!)
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!orgId,
  });

  // Delete integration
  const deleteIntegration = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("email_integrations")
        .delete()
        .eq("id", id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["email-integrations"] });
      toast({
        title: "Integration removed",
        description: "Email integration has been disconnected",
      });
    },
  });

  const handleConnectGmail = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("gmail-oauth-init");
      if (error) throw error;
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (error) {
      toast({
        title: "Failed to connect Gmail",
        description: error instanceof Error ? error.message : "Connection failed",
        variant: "destructive",
      });
    }
  };

  const handleConnectOutlook = async () => {
    try {
      const { data, error } = await supabase.functions.invoke("outlook-oauth-init");
      if (error) throw error;
      if (data?.authUrl) {
        window.location.href = data.authUrl;
      }
    } catch (error) {
      toast({
        title: "Failed to connect Outlook",
        description: error instanceof Error ? error.message : "Connection failed",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Info Card */}
      <Card className="border-primary/20 bg-primary/5">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Automated Email Scanning
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>Connect your Gmail or Outlook inbox to automatically scan for documents:</p>
          <ul className="list-disc list-inside space-y-1 text-muted-foreground">
            <li>Statements from custodians and brokers</li>
            <li>Capital call notices</li>
            <li>Transaction confirmations</li>
            <li>Performance reports</li>
          </ul>
          <p className="text-muted-foreground">
            Vintage will scan your inbox periodically and extract relevant documents based on sender and subject line patterns.
          </p>
        </CardContent>
      </Card>

      {/* Connected Integrations */}
      <div>
        <h3 className="text-lg font-semibold mb-4">Connected Email Accounts</h3>
        {integrations && integrations.length > 0 ? (
          <div className="space-y-3">
            {integrations.map((integration) => (
              <Card key={integration.id}>
                <CardContent className="pt-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Mail className="h-8 w-8 text-primary" />
                      <div>
                        <p className="font-medium">{integration.email_address}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline">{integration.provider}</Badge>
                          <Badge variant={integration.status === "active" ? "default" : "secondary"}>
                            {integration.status}
                          </Badge>
                          {integration.last_sync_at && (
                            <span className="text-sm text-muted-foreground">
                              Last synced: {new Date(integration.last_sync_at).toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteIntegration.mutate(integration.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                      Disconnect
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center py-8">
                <Mail className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="text-lg font-semibold mb-2">No email accounts connected</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Connect your Gmail or Outlook to enable automated document scanning
                </p>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Connect Buttons */}
      <div className="flex gap-3">
        <Button onClick={handleConnectGmail} className="flex-1">
          <Plus className="h-4 w-4" />
          Connect Gmail
        </Button>
        <Button onClick={handleConnectOutlook} variant="outline" className="flex-1">
          <Plus className="h-4 w-4" />
          Connect Outlook
        </Button>
      </div>
    </div>
  );
}
